import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { Check, Loader2, CreditCard, ArrowRight, Zap, Package } from 'lucide-react';
import { toast } from 'sonner';

const plans = [
  { id: "basic", name: "Starter", price: 49, credits: 1000, videos: 10, frequency: "2-3/semaine", features: ["10 videos/mois", "1 000 credits", "1 projet", "Publication YouTube", "Scripts IA GPT-5.2"] },
  { id: "pro", name: "Pro", price: 99, credits: 2500, videos: 25, frequency: "1/jour", features: ["25 videos/mois", "2 500 credits", "5 projets", "Multi-plateformes", "Scripts IA avances", "Entree vocale Whisper"], popular: true },
  { id: "max", name: "Business", price: 249, credits: 6000, videos: 60, frequency: "2/jour", features: ["60 videos/mois", "6 000 credits", "Projets illimites", "Toutes plateformes", "Optimisation IA", "Support prioritaire"] },
  { id: "enterprise", name: "Enterprise", price: 699, credits: 20000, videos: 200, frequency: "Sur mesure", features: ["200 videos/mois", "20 000 credits", "White-label", "API access", "Account manager dedie", "SLA garanti"] },
];

export default function PricingPage() {
  const { user, checkAuth } = useAuth();
  const [processing, setProcessing] = useState(null);
  const [checkingPayment, setCheckingPayment] = useState(false);
  const [rechargeProcessing, setRechargeProcessing] = useState(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('session_id');
    const type = params.get('type');
    if (sessionId) {
      setCheckingPayment(true);
      if (type === 'recharge') {
        pollRechargeStatus(sessionId, 0);
      } else {
        pollPaymentStatus(sessionId, 0);
      }
    }
  }, []);

  const pollPaymentStatus = async (sessionId, attempts) => {
    if (attempts >= 8) {
      setCheckingPayment(false);
      toast.error('Verification du paiement expire. Verifiez votre dashboard.');
      return;
    }
    try {
      const res = await api.get(`/subscriptions/status/${sessionId}`);
      if (res.data.payment_status === 'paid') {
        setCheckingPayment(false);
        toast.success('Paiement reussi ! Votre forfait est actif.');
        await checkAuth();
        // Clean URL
        window.history.replaceState({}, '', window.location.pathname);
      } else if (res.data.status === 'expired') {
        setCheckingPayment(false);
        toast.error('Session de paiement expiree.');
      } else {
        setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2500);
      }
    } catch {
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2500);
    }
  };

  const handleCheckout = async (planId) => {
    setProcessing(planId);
    try {
      const originUrl = window.location.origin;
      const res = await api.post('/subscriptions/checkout', {
        plan_type: planId,
        origin_url: originUrl,
      });
      if (res.data.url) {
        window.location.href = res.data.url;
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur de paiement');
      setProcessing(null);
    }
  };

  const pollRechargeStatus = async (sessionId, attempts) => {
    if (attempts >= 8) {
      setCheckingPayment(false);
      toast.error('Verification expiree.');
      return;
    }
    try {
      const res = await api.get(`/credits/recharge/status/${sessionId}`);
      if (res.data.payment_status === 'paid') {
        setCheckingPayment(false);
        toast.success('Credits recharges avec succes !');
        await checkAuth();
        window.history.replaceState({}, '', window.location.pathname);
      } else {
        setTimeout(() => pollRechargeStatus(sessionId, attempts + 1), 2500);
      }
    } catch {
      setTimeout(() => pollRechargeStatus(sessionId, attempts + 1), 2500);
    }
  };

  const handleRecharge = async (packId) => {
    setRechargeProcessing(packId);
    try {
      const originUrl = window.location.origin;
      const res = await api.post('/credits/recharge', {
        pack_id: packId,
        origin_url: originUrl,
      });
      if (res.data.url) {
        window.location.href = res.data.url;
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur de paiement');
      setRechargeProcessing(null);
    }
  };

  if (checkingPayment) {
    return (
      <div className="flex flex-col items-center justify-center h-64" data-testid="payment-checking">
        <div className="w-8 h-8 border-2 border-[#FF3300]/30 border-t-[#FF3300] rounded-full animate-spin mb-4" />
        <p className="text-white/60 text-sm">Verification du paiement en cours...</p>
      </div>
    );
  }

  return (
    <div data-testid="pricing-page">
      <div className="text-center mb-12">
        <h1 className="font-heading text-2xl sm:text-3xl lg:text-4xl tracking-tight font-medium text-white">
          {user ? 'Choisissez votre forfait' : 'Tarifs simples, resultats exponentiels'}
        </h1>
        <p className="text-sm text-white/40 mt-2">
          {user?.plan_type
            ? `Forfait actuel: ${user.plan_type} | ${user.credits_balance || 0} credits restants`
            : 'Chaque forfait inclut des credits mensuels pour la generation de videos IA.'
          }
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
        {plans.map((plan) => {
          const isCurrentPlan = user?.plan_type === plan.id;
          return (
            <div
              key={plan.id}
              className={`rounded-lg p-8 transition-all duration-300 flex flex-col ${
                plan.popular
                  ? 'bg-[#121212] border-2 border-white/20 relative'
                  : 'card-surface'
              }`}
              data-testid={`pricing-card-${plan.id}`}
            >
              {plan.popular && (
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 text-xs tracking-[0.15em] uppercase font-semibold text-black bg-white px-3 py-1 rounded-full">
                  Populaire
                </span>
              )}
              <h3 className="font-heading text-xl font-medium text-white mb-1">{plan.name}</h3>
              <div className="mb-6">
                <span className="font-heading text-4xl font-light text-white">${plan.price}</span>
                <span className="text-white/40 text-sm">/mois</span>
              </div>
              <ul className="space-y-3 mb-8 flex-1">
                {plan.features.map((f) => (
                  <li key={`${plan.name}-${f}`} className="flex items-start gap-2 text-sm text-white/60">
                    <Check className="w-4 h-4 text-[#34C759] flex-shrink-0 mt-0.5" />
                    {f}
                  </li>
                ))}
              </ul>
              {isCurrentPlan ? (
                <div className="w-full py-3 rounded-md text-sm font-medium text-center border border-[#34C759]/30 text-[#34C759]">
                  Forfait actuel
                </div>
              ) : (
                <button
                  onClick={() => handleCheckout(plan.id)}
                  disabled={processing === plan.id}
                  className={`w-full py-3 rounded-md text-sm font-medium transition-all flex items-center justify-center gap-2 disabled:opacity-50 ${
                    plan.popular ? 'btn-accent' : 'btn-outline'
                  }`}
                  data-testid={`checkout-btn-${plan.id}`}
                >
                  {processing === plan.id ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      Choisir {plan.name}
                      <ArrowRight className="w-4 h-4" />
                    </>
                  )}
                </button>
              )}
            </div>
          );
        })}
      </div>

      {/* Credit Recharge Packs */}
      <div className="max-w-4xl mx-auto mt-16" data-testid="credit-packs-section">
        <div className="text-center mb-8">
          <Package className="w-6 h-6 text-[#FF3300] mx-auto mb-3" />
          <h2 className="font-heading text-xl font-medium text-white">Recharger vos credits</h2>
          <p className="text-sm text-white/40 mt-1">Besoin de plus de credits ? Achetez un pack supplementaire.</p>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { id: "pack_200", name: "200 Credits", price: "9.99" },
            { id: "pack_500", name: "500 Credits", price: "19.99" },
            { id: "pack_1000", name: "1 000 Credits", price: "34.99" },
            { id: "pack_3000", name: "3 000 Credits", price: "89.99" },
          ].map(pack => (
            <div key={pack.id} className="card-surface rounded-lg p-5 text-center" data-testid={`credit-pack-${pack.id}`}>
              <p className="font-heading text-lg font-medium text-white">{pack.name}</p>
              <p className="text-2xl font-heading font-light text-[#FF3300] mt-2">${pack.price}</p>
              <button
                onClick={() => handleRecharge(pack.id)}
                disabled={rechargeProcessing === pack.id}
                className="mt-4 w-full py-2.5 rounded-md text-xs font-medium btn-outline flex items-center justify-center gap-1.5 disabled:opacity-50"
                data-testid={`recharge-btn-${pack.id}`}
              >
                {rechargeProcessing === pack.id ? (
                  <Loader2 className="w-3 h-3 animate-spin" />
                ) : (
                  <>
                    <Zap className="w-3 h-3" /> Acheter
                  </>
                )}
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Credit info */}
      <div className="max-w-2xl mx-auto mt-8 card-surface rounded-lg p-6 text-center">
        <p className="text-sm text-white/40 leading-relaxed">
          Chaque video consomme environ 100 credits. Les credits d'abonnement sont renouveles chaque mois.
          Les packs supplementaires ne sont pas lies a l'abonnement et restent valides indefiniment.
        </p>
      </div>
    </div>
  );
}
